import React, { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Command, CommandInput, CommandList, CommandGroup, CommandItem } from './ui/command';
import { ChevronDown, Loader2, Check } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Case-insensitive, locale-aware, numeric A-Z sort by company name.
 *
 * `sort_by=name` on the server is a MongoDB byte-order sort (case-sensitive),
 * so "Zebra Ltd" would sort ahead of "acme". The issue requires "acme" next to
 * "Acme Corp", so we re-sort the returned page client-side. Exported so the
 * ordering logic is unit-testable in isolation (the repo's test harness renders
 * raw DOM and avoids mounting cmdk/portals — see craco.config.js note).
 */
export function sortCompaniesByName(list) {
    return [...list].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'en', { numeric: true, sensitivity: 'base' })
    );
}

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 250;
const NONE_VALUE = '__none__';

/**
 * Type-to-search combobox for picking a Company (account) on the Contact page.
 *
 * Replaces the native <select> that rendered every company in creation order
 * with a silent 500-row cap. Results are server-searched
 * (GET /accounts?search=&sort_by=name&sort_direction=asc&page_size=50) and
 * re-sorted client-side for case-insensitive A-Z. Selecting an item marks the
 * field dirty (calls onChange) — it does NOT write to the backend; the page's
 * handleSave does that on explicit save, same contract as the old <select>.
 *
 * The trigger label comes from `selectedLabel` (the separately-fetched linked
 * account record) so the right company shows even when it is absent from the
 * current search results. Once the user picks a company inside the picker, the
 * trigger switches to that name so the edit state reflects the new selection
 * before it is saved.
 */
export default function CompanyPicker({
    value,
    onChange,
    selectedLabel,
    placeholder = 'No company',
    apiBase,
    getAuthHeader,
    testId,
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [total, setTotal] = useState(0);
    // 'idle' | 'loading' | 'ready' | 'error'
    const [status, setStatus] = useState('idle');
    // Name of the company chosen inside this picker session. Null until the
    // user picks something, so the trigger falls back to selectedLabel.
    const [pickedLabel, setPickedLabel] = useState(null);

    const debounceRef = useRef(null);
    const abortRef = useRef(null);
    const reqIdRef = useRef(0);

    const fetchCompanies = useCallback(async (search) => {
        // Cancel any in-flight request so a fast typist does not queue one
        // regex query per keystroke.
        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        const myReqId = ++reqIdRef.current;

        setStatus('loading');
        try {
            const res = await axios.get(`${apiBase}/accounts`, {
                params: {
                    sort_by: 'name',
                    sort_direction: 'asc',
                    page_size: PAGE_SIZE,
                    ...(search ? { search } : {}),
                },
                headers: getAuthHeader(),
                signal: controller.signal,
            });
            // Ignore stale responses — a newer request superseded this one.
            if (myReqId !== reqIdRef.current) return;
            const items = Array.isArray(res.data) ? res.data : (res.data?.data || []);
            const totalCount =
                typeof res.data?.total === 'number' ? res.data.total : items.length;
            setResults(sortCompaniesByName(items));
            setTotal(totalCount);
            setStatus('ready');
        } catch (err) {
            // Aborted requests are expected (superseded) — not an error state.
            if (controller.signal.aborted || err?.name === 'CanceledError') return;
            if (myReqId !== reqIdRef.current) return;
            setStatus('error');
        }
    }, [apiBase, getAuthHeader]);

    const handleOpenChange = (nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) {
            // Fresh search each time the popover opens.
            setQuery('');
            setResults([]);
            setPickedLabel(null);
            fetchCompanies('');
        } else if (abortRef.current) {
            // Drop any in-flight search when the popover closes so a late
            // response can't update state on a closed popover.
            abortRef.current.abort();
        }
    };

    const handleSearchChange = (q) => {
        setQuery(q);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            fetchCompanies(q);
        }, DEBOUNCE_MS);
    };

    const handleSelect = (id, name) => {
        setPickedLabel(name);
        onChange(id, name);
        setOpen(false);
    };

    // Clean up timers / in-flight requests on unmount.
    useEffect(() => () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (abortRef.current) abortRef.current.abort();
    }, []);

    const triggerLabel = (pickedLabel != null ? pickedLabel : selectedLabel) || placeholder;
    const showCapped = status === 'ready' && total > results.length;

    return (
        // modal: required for mouse-wheel scroll inside a Radix Dialog — the
        // Dialog's body scroll-lock bleeds into this Popover's Portal content
        // without it, making CommandList's overflow-y-auto inert. Safe but not
        // a no-op outside a Dialog: traps focus + disables outside interaction
        // while open (standard combobox behavior). Do not remove.
        <Popover open={open} onOpenChange={handleOpenChange} modal>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    role="combobox"
                    aria-expanded={open}
                    aria-haspopup="listbox"
                    data-testid={testId}
                    className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-sm border border-stone-200 rounded-md bg-white hover:border-stone-300 focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                >
                    <span className={cn('truncate text-left', !value && 'text-stone-400')}>
                        {triggerLabel}
                    </span>
                    <ChevronDown className="w-4 h-4 flex-shrink-0 opacity-50" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start" sideOffset={4}>
                <Command shouldFilter={false} label="Companies">
                    <CommandInput
                        placeholder="Search companies…"
                        value={query}
                        onValueChange={handleSearchChange}
                    />
                    <CommandList>
                        {status === 'loading' && (
                            <div data-testid="company-picker-loading" className="flex items-center gap-2 px-3 py-4 text-sm text-stone-500">
                                <Loader2 className="w-4 h-4 animate-spin" /> Searching…
                            </div>
                        )}
                        {status === 'error' && (
                            <div data-testid="company-picker-error" className="flex items-center justify-between px-3 py-4 text-sm text-stone-500">
                                <span>Search failed</span>
                                <button type="button" onClick={() => fetchCompanies(query)} className="text-stone-700 underline">
                                    Retry
                                </button>
                            </div>
                        )}
                        {status === 'ready' && results.length === 0 && (
                            <div data-testid="company-picker-empty" className="px-3 py-4 text-sm text-stone-500">
                                No companies match &ldquo;{query}&rdquo;.
                            </div>
                        )}
                        {status !== 'error' && (
                            <CommandGroup>
                                <CommandItem
                                    value={NONE_VALUE}
                                    onSelect={() => handleSelect('', '')}
                                    data-testid="company-picker-none"
                                >
                                    <Check className={cn('mr-2 h-4 w-4 flex-shrink-0', !value ? 'opacity-100' : 'opacity-0')} />
                                    <span className="italic text-stone-400">No company</span>
                                </CommandItem>
                                {results.map((a) => (
                                    <CommandItem
                                        key={a.account_id}
                                        value={a.account_id}
                                        onSelect={() => handleSelect(a.account_id, a.name)}
                                        data-testid={`company-picker-item-${a.account_id}`}
                                    >
                                        <Check className={cn('mr-2 h-4 w-4 flex-shrink-0', value === a.account_id ? 'opacity-100' : 'opacity-0')} />
                                        <span className="truncate">{a.name}</span>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        )}
                        {showCapped && (
                            <div data-testid="company-picker-capped" className="px-3 py-2 text-xs text-stone-400 border-t border-stone-100">
                                Showing first {results.length} — type to narrow.
                            </div>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
